import { useEffect, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PlanFeaturesErrorState } from 'app/shared-components/PlusPaywall';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

import { ONBOARDING_PATHS } from './constants/paths';
import { HOME_PAGE_BUILDER_BLURB } from './constants/onboarding-steps';
import { OnboardingLayout } from './components/OnboardingLayout';
import { useWebBuilderAccess, useIsBespoke } from '../../hooks/useWebBuilderAccess';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';
import { useOnboarding } from './store/onboarding-store';

type HomePageOption = 'builder' | 'own';

const toOption = (landingType: 'in-app' | 'external'): HomePageOption =>
  landingType === 'in-app' ? 'builder' : 'own';

const toLandingType = (option: HomePageOption): 'in-app' | 'external' =>
  option === 'builder' ? 'in-app' : 'external';

const HomePageStep = (): ReactElement | null => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { landingType, externalHomepageUrl, setLandingType, setExternalHomepageUrl } =
    useOnboarding();
  const { hasWebBuilderAccess } = useWebBuilderAccess();
  const { trackDestinationSaved } = useOnboardingTracker();
  const {
    isBespoke,
    isLoading: isBespokeLoading,
    isError: isBespokeError,
    retry: retryBespoke,
  } = useIsBespoke();

  // Bespoke organisations always use the builder homepage — skip this step entirely.
  useEffect(() => {
    if (isBespoke) {
      setLandingType('in-app');
      navigate(
        { pathname: ONBOARDING_PATHS.domain, search: searchParams.toString() },
        { replace: true }
      );
    }
  }, [isBespoke, setLandingType, navigate, searchParams]);

  if (isBespokeError) return <PlanFeaturesErrorState onRetry={retryBespoke} />;
  if (isBespokeLoading || isBespoke) return null;

  const selected = toOption(landingType);

  // The preview step is skipped for organisations that already own the builder, so Back has
  // to reach past it.
  const backTo = hasWebBuilderAccess ? ONBOARDING_PATHS.logo : ONBOARDING_PATHS.preview;

  const handleContinue = () => {
    if (landingType === 'external' && !externalHomepageUrl.trim()) {
      enqueueSnackbar('Please enter your home page URL', { variant: 'error' });
      return;
    }
    trackDestinationSaved();
    navigate({ pathname: ONBOARDING_PATHS.domain, search: searchParams.toString() });
  };

  return (
    <OnboardingLayout
      activeStep={3}
      backTo={backTo}
      primaryLabel="Continue"
      onPrimary={handleContinue}
    >
      <div className="vtwo:text-center vtwo:mb-10">
        <h1 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-3">Where should visitors land?</h1>
        <p className="vtwo:text-base vtwo:text-gray-600">Use our page or bring your own</p>
      </div>

      <RadioGroup
        value={selected}
        onValueChange={(val) => setLandingType(toLandingType(val as HomePageOption))}
        className="vtwo:max-w-3xl vtwo:mx-auto vtwo:gap-4"
      >
        <Label
          htmlFor="option-builder"
          className={cn(
            'vtwo:flex vtwo:items-center vtwo:justify-between vtwo:rounded-xl vtwo:border vtwo:p-6 vtwo:cursor-pointer vtwo:transition-colors vtwo:font-normal',
            selected === 'builder' ? 'vtwo:border-featured' : 'vtwo:border-gray-200'
          )}
        >
          <div className="vtwo:flex vtwo:items-start vtwo:gap-3">
            <RadioGroupItem value="builder" id="option-builder" className="vtwo:mt-0.5" />
            <div>
              <p className="vtwo:text-sm vtwo:font-bold">Use page with builder</p>
              <p className="vtwo:text-sm vtwo:text-gray-600">{HOME_PAGE_BUILDER_BLURB}</p>
            </div>
          </div>
        </Label>

        <div
          className={cn(
            'vtwo:rounded-xl vtwo:border vtwo:p-6 vtwo:transition-colors',
            selected === 'own' ? 'vtwo:border-featured' : 'vtwo:border-gray-200'
          )}
        >
          <Label
            htmlFor="option-own"
            className="vtwo:flex vtwo:items-start vtwo:gap-3 vtwo:cursor-pointer vtwo:font-normal"
          >
            <RadioGroupItem value="own" id="option-own" className="vtwo:mt-0.5" />
            <div>
              <p className="vtwo:text-sm vtwo:font-bold">Use own home page</p>
              <p className="vtwo:text-sm vtwo:text-gray-600">
                Our prescription, services, clinic and other journeys can be linked from your home
                page.
              </p>
            </div>
          </Label>
          {selected === 'own' && (
            <div className="vtwo:mt-4 vtwo:ml-7">
              <Input
                placeholder="Your home page URL"
                className="vtwo:mb-2"
                value={externalHomepageUrl}
                onChange={(e) => setExternalHomepageUrl(e.target.value)}
              />
            </div>
          )}
        </div>
      </RadioGroup>
    </OnboardingLayout>
  );
};

export default HomePageStep;
